// PHAS-1199: refund protect / charity / promo toggles re-create the TS order. These keep the
// order id and the Stripe intent pointing at the same order while that happens.

// Each postOrder takes a ticket; only the newest may write the order, so a slow response
// from an earlier toggle can't overwrite the order the athlete is about to pay for.
export function createOrderSequencer() {
  let latest = 0;
  return {
    next: () => ++latest,
    isLatest: (ticket) => ticket === latest,
  };
}

// Stripe redirects (3DS, wallets) land back on return_url. localStorage can be gone by then
// (in-app browsers, private mode), so the order id rides along in the URL too.
// ponytail: in-memory, per tab, TTL-bounded so a long-idle staged TS order is re-created.
// The real fix is creating the order only at Pay (PHAS-1199 follow-up).
export function createOrderCache(ttlMs, now = Date.now, max = 8) {
  const entries = new Map();
  return {
    get(key) {
      const e = entries.get(key);
      return e && now() - e.at <= ttlMs ? e.result : null;
    },
    set(key, result) {
      entries.delete(key);
      entries.set(key, { result, at: now() });
      if (entries.size > max) entries.delete(entries.keys().next().value);
    },
    clear() {
      entries.clear();
    },
  };
}

// Deferred-intent Elements must mirror the TS-created PaymentIntent, whose shape differs per site
// (DEKA: card + off_session, TM/EU: link/klarna/ideal/...). card_present is terminal-only.
export function deferredElementsOptions(intent, amountCents) {
  const options = {
    mode: "payment",
    amount: amountCents || intent.amount,
    currency: intent.currency.toLowerCase(),
    paymentMethodTypes: intent.payment_method_types.filter((t) => t !== "card_present"),
    appearance: { theme: "stripe" },
  };
  if (intent.setup_future_usage) options.setupFutureUsage = intent.setup_future_usage;
  return options;
}

// api-gate resolves each ticket id as `${country_code}${id}`, so one cart item from another
// region fails the whole order with MM0002 (or matches another region's ticket). An
// all-foreign cart is passed through unchanged: reading it as empty would drop the cart.
// ponytail: foreign items in a mixed cart are lost when the order completes (deleteCart);
// 2 such carts in 14 days. Split the cart per region if that grows.
export function ticketsForRegion(tickets, region) {
  const r = String(region || "").toUpperCase();
  const own = tickets.filter((t) => !t.country || t.country.toUpperCase() === r);
  return own.length ? own : tickets;
}

// Links (e.g. the SFMC abandoned-cart email) can open /EU/checkout for an IE-only cart.
// When every ticket belongs to one other region, the cart's region is the real one.
export function regionForCart(tickets, region) {
  const r = String(region || "").toUpperCase();
  const countries = new Set(tickets.map((t) => String(t.country || "").toUpperCase()).filter(Boolean));
  return countries.size === 1 && !countries.has(r) ? [...countries][0] : r;
}

export function withOrderId(url, orderId) {
  if (!orderId) return url;
  const u = new URL(url);
  u.searchParams.set("order_id", orderId);
  return u.toString();
}

export function restoreOrderId(storage, query) {
  const fromUrl = query?.order_id;
  if (fromUrl) storage.setItem("orderId", fromUrl);
  return storage.getItem("orderId");
}

// Once Stripe has taken the money, a second Pay must only retry /complete for that order,
// never create a new order + intent (that charged the card twice).
// ponytail: matched on amount + 30 min TTL, not cart contents. A mismatch falls back to a
// normal payment, so the failure mode is the old behaviour, never completing the wrong order.
const PAID_KEY = "checkoutPaid";
const PAID_TTL_MS = 30 * 60 * 1000;

export function rememberPaid(storage, orderId, paymentIntentId, amountCents, now = Date.now()) {
  if (orderId && paymentIntentId)
    storage.setItem(PAID_KEY, JSON.stringify({ orderId: String(orderId), paymentIntentId, amountCents, at: now }));
}

export function unfinishedPayment(storage, amountCents, now = Date.now()) {
  let paid;
  try {
    paid = JSON.parse(storage.getItem(PAID_KEY));
  } catch {
    return null;
  }
  if (!paid || now - paid.at > PAID_TTL_MS || paid.amountCents !== amountCents) return null;
  return paid;
}

export function forgetPaid(storage) {
  storage.removeItem(PAID_KEY);
}
